export const SUPER_REGION_OPTIONS = [
  { value: "wnam", label: "Western North America" },
  { value: "enam", label: "Eastern North America" },
  { value: "weur", label: "Western Europe" },
  { value: "eeur", label: "Eastern Europe & Middle East" },
  { value: "apac", label: "Asia Pacific" },
  { value: "oc",   label: "Oceania" },
  { value: "latam", label: "Latin America" },
] as const;

export const AWS_GPU_REGIONS = [
  { value: "us-east-1", label: "US East (N. Virginia)", gpu: "G5" },
  { value: "us-west-2", label: "US West (Oregon)", gpu: "G5" },
  { value: "eu-west-2", label: "Europe (London)", gpu: "G5" },
  { value: "eu-west-3", label: "Europe (Paris)", gpu: "G5" },
  { value: "ap-northeast-1", label: "Asia Pacific (Tokyo)", gpu: "G5" },
  { value: "ap-southeast-2", label: "Asia Pacific (Sydney)", gpu: "G5" },
] as const;

// GCP regions available for GPU pool backends.
//
// `families` is what GCP's catalog actually offers in the region, measured
// 2026-09-02 against research-omniverse rather than transcribed from docs
// (`gcloud compute machine-types list --filter='name~^g2-'` / `^g4-`, and
// `gcloud compute accelerator-types list` for the n1-attached T4 vWS). It
// drives the machine-type dropdown, so a shape GCP cannot build here is not
// offered here. `gpu` is the human-readable echo of the same fact.
//
// AVAILABILITY IS NOT QUOTA. Every region below can still fail with
// "Quota exceeded" — a separate, per-region, per-GPU-family grant. And the
// vWS and bare parts of the same GPU hold SEPARATE grants, so which one a
// backend requests (`gcpGpuVariant`) decides which pool it spends.
// RTX PRO 6000 snapshot, 2026-09-02, as vWS / bare:
//
//   us-west4, us-east1, asia-southeast1, asia-east1     8 / 8
//   us-east4                                            8 / 0
//   europe-west1, europe-west2                          4 / 8
//   us-west1                                            0 / 8
//   us-central1, europe-west4, asia-northeast1,
//     me-central2, southamerica-east1                   0 / 0
//
// us-west1 is the one to notice: it has G4 hardware and a full bare grant but
// no vWS at all, so it is reachable only with `gcpGpuVariant: "standard"`.
// That trades away WDDM, which DirectX and NVENC need — worth a measurement
// before committing a pool to it, since our g2 fleet has always run the bare
// L4 and renders fine, which suggests the WDDM story is not as simple as the
// vWS naming implies.
//
// L4 runs the other way: the bare grant is 64 in us-central1, us-west1 and
// europe-west4 against a flat 32 default for L4 vWS, so switching a large g2
// pool to vWS lowers its ceiling.
//
// Deliberately NOT encoded as a field: quota moves, and a stale number in
// the UI is worse than none. Re-measure with:
//   gcloud compute regions describe <region> --flatten='quotas[]' \
//     --format='csv(quotas.metric,quotas.limit,quotas.usage)'   # L4/T4 only
// and, for G4 (newer families are enforced via GPUS-PER-GPU-FAMILY, which
// the legacy regions-describe list does not carry):
//   curl -H "Authorization: Bearer $(gcloud auth print-access-token)" \
//     "https://cloudquotas.googleapis.com/v1/projects/<p>/locations/global/\
// services/compute.googleapis.com/quotaInfos/GPUS-PER-GPU-FAMILY-per-project-region"
//
// Africa (africa-south1) and New Zealand are intentionally omitted:
// africa-south1 has zero GPUs of any kind, and GCP has no NZ region.
export const GCP_GPU_REGIONS = [
  // North America
  { value: "us-west1",             label: "US West (Oregon)",          gpu: "L4 · T4 · RTX-Pro", families: ["g2", "g4", "n1"] },
  { value: "us-west2",             label: "US West (Los Angeles)",     gpu: "T4",                families: ["n1"] },
  { value: "us-west4",             label: "US West (Las Vegas)",       gpu: "L4 · T4 · RTX-Pro", families: ["g2", "g4", "n1"] },
  { value: "us-central1",          label: "US Central (Iowa)",         gpu: "L4 · T4 · RTX-Pro", families: ["g2", "g4", "n1"] },
  { value: "us-east1",             label: "US East (S. Carolina)",     gpu: "L4 · T4 · RTX-Pro", families: ["g2", "g4", "n1"] },
  { value: "us-east4",             label: "US East (N. Virginia)",     gpu: "L4 · T4 · RTX-Pro", families: ["g2", "g4", "n1"] },
  // Europe
  { value: "europe-west1",         label: "Europe West (Belgium)",     gpu: "L4 · T4 · RTX-Pro", families: ["g2", "g4", "n1"] },
  { value: "europe-west2",         label: "Europe West (London)",      gpu: "L4 · T4 · RTX-Pro", families: ["g2", "g4", "n1"] },
  { value: "europe-west3",         label: "Europe West (Frankfurt)",   gpu: "L4 · T4",           families: ["g2", "n1"] },
  { value: "europe-west4",         label: "Europe West (Netherlands)", gpu: "L4 · T4 · RTX-Pro", families: ["g2", "g4", "n1"] },
  { value: "europe-west6",         label: "Europe West (Zurich)",      gpu: "L4",                families: ["g2"] },
  // Middle East
  { value: "me-west1",             label: "Middle East (Tel Aviv)",    gpu: "T4",                families: ["n1"] },
  { value: "me-central2",          label: "Middle East (Dammam)",      gpu: "L4 · RTX-Pro",      families: ["g2", "g4"] },
  // Asia-Pacific
  { value: "asia-northeast1",      label: "Asia (Tokyo)",              gpu: "L4 · T4 · RTX-Pro", families: ["g2", "g4", "n1"] },
  { value: "asia-northeast3",      label: "Asia (Seoul)",              gpu: "L4 · T4",           families: ["g2", "n1"] },
  { value: "asia-southeast1",      label: "Asia (Singapore)",          gpu: "L4 · T4 · RTX-Pro", families: ["g2", "g4", "n1"] },
  { value: "asia-east1",           label: "Asia (Taiwan)",             gpu: "L4 · T4 · RTX-Pro", families: ["g2", "g4", "n1"] },
  { value: "australia-southeast1", label: "Australia (Sydney)",        gpu: "T4",                families: ["n1"] },
  // South America
  { value: "southamerica-east1",   label: "South America (São Paulo)", gpu: "T4 · RTX-Pro",      families: ["g4", "n1"] },
] as const;

export const COREWEAVE_REGIONS = [
  { value: "ORD1", label: "US Central (Chicago)", gpu: "H100/A100/A40" },
  { value: "LGA1", label: "US East (New Jersey)", gpu: "H100/A100/A40" },
  { value: "LAS1", label: "US West (Las Vegas)", gpu: "H100/A100/A40" },
] as const;

// Machine-type dropdowns for the InfrastructureEditor. These are UI
// hints only — any valid string a user enters manually still works via
// the "(custom)" fallback option in the editor. CoreWeave is not
// represented here: its machine spec is expressed via separate per-
// backend cpu/memory/gpu fields on ManagedBackendConfig, not an
// `instanceType` string.
// Every shape is the whole published family, taken from GCP's catalog on
// 2026-09-02 (`gcloud compute machine-types describe <name>` — vCPU, RAM and
// GPU count all read off the API, not the pricing page). `family` is matched
// against a region's `families` above so the dropdown only offers shapes the
// selected region can actually build.
//
// One worker serves one session, so the multi-GPU sizes are not a capacity
// upgrade for a normal streaming pool — they are here for the cases where
// they are the point (multi-tenant hosts, side-by-side render jobs) and
// because a size GCP sells should not be unreachable from the console.
export const GCP_MACHINE_TYPES = [
  // g2 — built-in NVIDIA L4, 24 GB VRAM each. Note -32 is the odd one out:
  // twice the vCPUs of -24 but back down to a single L4.
  { value: "g2-standard-4",   family: "g2", label: "g2-standard-4   (4 vCPU, 16 GB, 1× L4)" },
  { value: "g2-standard-8",   family: "g2", label: "g2-standard-8   (8 vCPU, 32 GB, 1× L4)" },
  { value: "g2-standard-12",  family: "g2", label: "g2-standard-12  (12 vCPU, 48 GB, 1× L4)" },
  { value: "g2-standard-16",  family: "g2", label: "g2-standard-16  (16 vCPU, 64 GB, 1× L4)" },
  { value: "g2-standard-24",  family: "g2", label: "g2-standard-24  (24 vCPU, 96 GB, 2× L4)" },
  { value: "g2-standard-32",  family: "g2", label: "g2-standard-32  (32 vCPU, 128 GB, 1× L4)" },
  { value: "g2-standard-48",  family: "g2", label: "g2-standard-48  (48 vCPU, 192 GB, 4× L4)" },
  { value: "g2-standard-96",  family: "g2", label: "g2-standard-96  (96 vCPU, 384 GB, 8× L4)" },
  // g4 — built-in RTX PRO 6000 Blackwell, 96 GB VRAM per whole GPU. The
  // first three are vGPU slices of ONE card (GCP's own description reads
  // "1/8 NVIDIA RTX PRO 6000 GPU"), which is why they still request an
  // accelerator count of 1; -96 and up carry whole extra cards.
  { value: "g4-standard-6",   family: "g4", label: "g4-standard-6   (6 vCPU, 22 GB, ⅛ RTX-Pro)" },
  { value: "g4-standard-12",  family: "g4", label: "g4-standard-12  (12 vCPU, 45 GB, ¼ RTX-Pro)" },
  { value: "g4-standard-24",  family: "g4", label: "g4-standard-24  (24 vCPU, 90 GB, ½ RTX-Pro)" },
  { value: "g4-standard-48",  family: "g4", label: "g4-standard-48  (48 vCPU, 180 GB, 1× RTX-Pro)" },
  { value: "g4-standard-96",  family: "g4", label: "g4-standard-96  (96 vCPU, 360 GB, 2× RTX-Pro)" },
  { value: "g4-standard-192", family: "g4", label: "g4-standard-192 (192 vCPU, 720 GB, 4× RTX-Pro)" },
  { value: "g4-standard-384", family: "g4", label: "g4-standard-384 (384 vCPU, 1440 GB, 8× RTX-Pro)" },
  // n1 — no built-in GPU; the backend attaches nvidia-tesla-t4-vws.
  { value: "n1-standard-4",   family: "n1", label: "n1-standard-4   (4 vCPU, 15 GB, + T4 vWS)" },
  { value: "n1-standard-8",   family: "n1", label: "n1-standard-8   (8 vCPU, 30 GB, + T4 vWS)" },
  { value: "n1-standard-16",  family: "n1", label: "n1-standard-16  (16 vCPU, 60 GB, + T4 vWS)" },
] as const;

export const AWS_MACHINE_TYPES = [
  // g6 family — NVIDIA L4
  { value: "g6.xlarge",    label: "g6.xlarge    (4 vCPU, 16 GB, 1× L4)" },
  { value: "g6.2xlarge",   label: "g6.2xlarge   (8 vCPU, 32 GB, 1× L4)" },
  { value: "g6.4xlarge",   label: "g6.4xlarge   (16 vCPU, 64 GB, 1× L4)" },
  // g5 family — NVIDIA A10G
  { value: "g5.xlarge",    label: "g5.xlarge    (4 vCPU, 16 GB, 1× A10G)" },
  { value: "g5.2xlarge",   label: "g5.2xlarge   (8 vCPU, 32 GB, 1× A10G)" },
  { value: "g5.4xlarge",   label: "g5.4xlarge   (16 vCPU, 64 GB, 1× A10G)" },
  // g4dn family — NVIDIA T4
  { value: "g4dn.xlarge",  label: "g4dn.xlarge  (4 vCPU, 16 GB, 1× T4)" },
  { value: "g4dn.2xlarge", label: "g4dn.2xlarge (8 vCPU, 32 GB, 1× T4)" },
] as const;

// CoreWeave GPU types. Unlike AWS/GCP where the machine type selects
// both CPU and GPU, CoreWeave's VirtualServer CRD takes a GPU type
// string separately from the CPU/memory counts (which are independent
// per-backend fields: cwCpuCount, cwMemoryGb). The `value` here is
// exactly the string CoreWeave's API expects in `spec.gpu.type`.
export const COREWEAVE_GPU_TYPES = [
  // RTX workstation / A-series — most common for streaming workloads
  { value: "RTX_A4000",        label: "RTX A4000  (16 GB VRAM)" },
  { value: "RTX_A5000",        label: "RTX A5000  (24 GB VRAM)" },
  { value: "RTX_A6000",        label: "RTX A6000  (48 GB VRAM)" },
  // Data center — A series
  { value: "A40",              label: "A40        (48 GB VRAM)" },
  { value: "A100",             label: "A100 PCIe  (40 GB VRAM)" },
  { value: "A100_NVLINK",      label: "A100 NVLINK (40 GB VRAM)" },
  { value: "A100_NVLINK_80GB", label: "A100 NVLINK 80 GB" },
  // Data center — H series (latest generation)
  { value: "H100",             label: "H100 PCIe  (80 GB VRAM)" },
  { value: "H100_NVLINK",      label: "H100 NVLINK (80 GB VRAM)" },
  // Legacy Quadro — kept for backwards compat
  { value: "Quadro_RTX_4000",  label: "Quadro RTX 4000 (8 GB VRAM)" },
  { value: "Quadro_RTX_5000",  label: "Quadro RTX 5000 (16 GB VRAM)" },
] as const;

export const CLOUD_OPTIONS = [
  { value: "aws", label: "Amazon Web Services" },
  { value: "gcp", label: "Google Cloud Platform" },
  { value: "coreweave", label: "CoreWeave" },
] as const;

export const ALLOCATION_STRATEGIES = [
  { value: "balanced", label: "Balanced" },
  { value: "cost-priority", label: "Cost Priority" },
  { value: "latency-priority", label: "Latency Priority" },
] as const;

export type SuperRegion = typeof SUPER_REGION_OPTIONS[number]["value"];
export type CloudProvider = typeof CLOUD_OPTIONS[number]["value"];
export type AllocationStrategy = typeof ALLOCATION_STRATEGIES[number]["value"];
